import { useState, useEffect, useCallback } from "react";
import { WebAvatar } from "@/components/WebAvatar";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Briefcase, MessageSquare, Clock, MapPin, Users, ChevronRight, Phone, CheckCircle, RotateCcw, Trash2, ChevronLeft } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface InterestedTrade {
  id: number;
  createdAt: string;
  userId: number;
  name?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  conversationId?: number | null;
}

interface MyJob {
  id: number;
  title: string;
  category: string | null;
  town: string;
  urgency: string | null;
  budgetRange: string | null;
  status: string;
  approved: boolean;
  createdAt: string;
  interestedCount: number;
  interested: InterestedTrade[];
}

const URGENCY_CONFIG: Record<string, { label: string; colour: string }> = {
  emergency:  { label: "Emergency",    colour: "bg-red-100 text-red-700 border-red-200" },
  within_24h: { label: "Within 24h",  colour: "bg-orange-100 text-orange-700 border-orange-200" },
  this_week:  { label: "This week",   colour: "bg-amber-100 text-amber-700 border-amber-200" },
  this_month: { label: "This month",  colour: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  flexible:   { label: "Flexible",    colour: "bg-green-100 text-green-700 border-green-200" },
};

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
function quoteTimeRemaining(createdAt: string): { label: string; colour: string; expired: boolean } {
  const remaining = new Date(createdAt).getTime() + SEVEN_DAYS_MS - Date.now();
  if (remaining <= 0) return { label: "Expired", colour: "text-slate-400", expired: true };
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((remaining % (60 * 60 * 1000)) / 60000);
  const label = days > 0 ? `${days}d ${hours}h left` : hours > 0 ? `${hours}h ${mins}m left` : `${mins}m left`;
  const colour = days >= 3 ? "text-emerald-600" : days >= 1 ? "text-amber-600" : "text-red-500";
  return { label, colour, expired: false };
}

function TradeCard({ trade }: { trade: InterestedTrade; jobId: number }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
      <WebAvatar name={trade.name ?? "T"} avatarUrl={trade.avatarUrl} size={40} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 text-sm">{trade.name ?? "Tradesperson"}</p>
        <p className="text-xs text-slate-400">{timeAgo(trade.createdAt)}</p>
      </div>
      <div className="flex items-center gap-2">
        {trade.phone && (
          <a
            href={`tel:${trade.phone}`}
            className="p-2 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
            title="Call"
          >
            <Phone size={15} />
          </a>
        )}
        {trade.conversationId ? (
          <Link href={`/messages?conversationId=${trade.conversationId}`}>
            <button className="p-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors" title="Message">
              <MessageSquare size={15} />
            </button>
          </Link>
        ) : (
          <button
            className="p-2 rounded-xl bg-slate-100 text-slate-400 cursor-not-allowed"
            title="No conversation yet"
            disabled
          >
            <MessageSquare size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function MyJobs() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [jobs, setJobs] = useState<MyJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [closingId, setClosingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/my-jobs", { headers: authHeaders() });
      const data = await res.json() as { items: MyJob[] };
      setJobs(data.items ?? []);
    } catch { setJobs([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) void fetchJobs(); }, [user, fetchJobs]);

  const handleToggleStatus = async (job: MyJob) => {
    const newStatus = job.status === "closed" ? "open" : "closed";
    setClosingId(job.id);
    try {
      await fetch(`/api/jobs/${job.id}/status`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: newStatus } : j));
    } finally {
      setClosingId(null);
    }
  };

  const handleDelete = async (job: MyJob) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    setDeletingId(job.id);
    try {
      await fetch(`/api/jobs/${job.id}`, { method: "DELETE", headers: authHeaders() });
      setJobs(prev => prev.filter(j => j.id !== job.id));
      if (expanded === job.id) setExpanded(null);
    } finally {
      setDeletingId(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🔧</div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Sign in to view your quotes</h1>
          <div className="flex gap-3 mt-4">
            <Link href="/login" className="flex-1"><Button variant="outline" className="w-full">Login</Button></Link>
            <Link href="/signup" className="flex-1"><Button className="w-full bg-primary hover:bg-primary/90">Sign Up</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-primary px-4 py-5">
        <div className="container mx-auto max-w-2xl">
          <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-white/80 hover:text-white mb-3 transition-colors">
            <ChevronLeft size={18} /> Back
          </button>
        </div>
        <div className="container mx-auto max-w-2xl flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">My Quote Requests</h1>
            <p className="text-white/70 text-sm">Track and manage your requests to local tradespeople</p>
          </div>
          <Button
            onClick={() => setLocation("/post-job")}
            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold text-sm"
          >
            + New Request
          </Button>
        </div>
      </div>

      <div className="container mx-auto max-w-2xl px-4 py-6 space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-5 animate-pulse">
                <div className="h-5 bg-slate-200 rounded w-2/3 mb-3" />
                <div className="h-3 bg-slate-200 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Briefcase size={32} className="text-primary" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">No quote requests yet</h2>
            <p className="text-slate-500 text-sm mb-6">Request a quote and local tradespeople will be notified and express interest.</p>
            <Button onClick={() => setLocation("/post-job")} className="bg-primary hover:bg-primary/90">
              Request Your First Quote
            </Button>
          </div>
        ) : (
          jobs.map(job => {
            const urgency = URGENCY_CONFIG[job.urgency ?? ""] ?? null;
            const isExpanded = expanded === job.id;
            const isClosed = job.status === "closed";
            return (
              <div key={job.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-opacity ${isClosed ? "opacity-60 border-slate-200" : "border-slate-100"}`}>
                <button
                  className="w-full text-left p-5"
                  onClick={() => setExpanded(isExpanded ? null : job.id)}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-bold text-slate-800 leading-snug">{job.title}</h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isClosed ? (
                        <span className="text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full">
                          Closed
                        </span>
                      ) : !job.approved ? (
                        <span className="text-[10px] font-bold bg-amber-50 dark:bg-amber-900/20 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full">
                          Pending
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full">
                          Open
                        </span>
                      )}
                      <ChevronRight
                        size={16}
                        className={`text-slate-300 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {urgency && (
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${urgency.colour}`}>
                        {urgency.label}
                      </span>
                    )}
                    {job.category && (
                      <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full">{job.category}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><MapPin size={11} />{job.town}</span>
                    <span className="flex items-center gap-1"><Clock size={11} />{timeAgo(job.createdAt)}</span>
                    {!isClosed && (() => { const t = quoteTimeRemaining(job.createdAt); return (
                      <span className={`flex items-center gap-1 font-semibold ${t.colour}`}>
                        <Clock size={11} />{t.label}
                      </span>
                    ); })()}
                    {!isClosed && (
                      <span className="flex items-center gap-1 font-semibold text-primary">
                        <Users size={11} />
                        {job.interestedCount === 0
                          ? "No interest yet"
                          : `${job.interestedCount} tradesperson${job.interestedCount > 1 ? "s" : ""} interested`
                        }
                      </span>
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100 p-4 space-y-4">
                    {job.interested.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Interested Tradespeople</p>
                        {job.interested.map(trade => (
                          <TradeCard key={trade.id} trade={trade} jobId={job.id} />
                        ))}
                      </div>
                    )}

                    {job.interested.length === 0 && !isClosed && (
                      <p className="text-slate-400 text-sm text-center py-4">
                        No tradespeople have expressed interest yet. You'll be notified as soon as one unlocks your request.
                      </p>
                    )}

                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleDelete(job); }}
                        disabled={deletingId === job.id}
                        className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleToggleStatus(job); }}
                        disabled={closingId === job.id}
                        className={`flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl border transition-colors disabled:opacity-50 ${
                          isClosed
                            ? "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                            : "border-slate-200 text-slate-600 bg-slate-50 hover:bg-slate-100"
                        }`}
                      >
                        {isClosed
                          ? <><RotateCcw size={14} /> Reopen Request</>
                          : <><CheckCircle size={14} /> Mark as Complete</>
                        }
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
